import React, { useState, useEffect } from 'react';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../../../lib/api';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Tag, Plus, Pencil, Trash2, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const COLOR_OPTIONS = [
  { value: '#ef4444', label: 'Rot' },
  { value: '#f97316', label: 'Orange' },
  { value: '#eab308', label: 'Gelb' },
  { value: '#22c55e', label: 'Grün' },
  { value: '#3b82f6', label: 'Blau' },
  { value: '#8b5cf6', label: 'Lila' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#64748b', label: 'Grau' },
];

export default function AdminArticleGroups({ embedded = false }) {
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', color: '#3b82f6' });

  useEffect(() => { loadGroups(); }, []);

  const loadGroups = async () => {
    setIsLoading(true);
    try {
      setGroups(await getCategories());
    } catch {
      toast.error('Fehler beim Laden');
    } finally {
      setIsLoading(false);
    }
  };

  const openDialog = (group = null) => {
    setEditing(group);
    setForm(group ? { name: group.name, color: group.color || '#3b82f6' } : { name: '', color: '#3b82f6' });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Bitte Namen eingeben'); return; }
    try {
      if (editing) {
        await updateCategory(editing.id, form);
        toast.success('Artikelgruppe aktualisiert');
      } else {
        await createCategory(form);
        toast.success('Artikelgruppe erstellt');
      }
      setShowDialog(false);
      loadGroups();
    } catch {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Artikelgruppe wirklich löschen?')) return;
    try {
      await deleteCategory(id);
      toast.success('Artikelgruppe gelöscht');
      loadGroups();
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'min-h-[220px]' : 'h-screen'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-[#1a2f38]" />
      </div>
    );
  }

  return (
    <div className={`${embedded ? 'space-y-6' : 'space-y-6 p-6'}`}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a2f38]">Artikelgruppen</h1>
          <p className="text-slate-500">{groups.length} Gruppen</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadGroups}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
          <Button className="bg-[#1a2f38] hover:bg-[#2a4048]" onClick={() => openDialog()}>
            <Plus className="w-4 h-4 mr-2" />
            Neue Gruppe
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {groups.map((group) => (
          <Card key={group.id} className="shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: group.color || '#3b82f6' }}
                >
                  <Tag className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{group.name}</p>
                  <p className="text-sm text-slate-400">Sortierung: {group.sort_order ?? 0}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => openDialog(group)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={() => handleDelete(group.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {groups.length === 0 && (
        <div className="text-center py-16">
          <Tag className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">Keine Artikelgruppen vorhanden</p>
          <Button className="mt-4 bg-[#1a2f38]" onClick={() => openDialog()}>
            Erste Gruppe erstellen
          </Button>
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Gruppe bearbeiten' : 'Neue Artikelgruppe'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="z.B. Getränke"
                autoFocus
              />
            </div>
            <div>
              <Label>Farbe</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {COLOR_OPTIONS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setForm({ ...form, color: c.value })}
                    title={c.label}
                    className={`w-9 h-9 rounded-lg border-2 transition-transform ${
                      form.color === c.value ? 'border-slate-800 scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: c.value }}
                  />
                ))}
              </div>
            </div>
            <Button onClick={handleSave} className="w-full bg-[#1a2f38]">
              {editing ? 'Speichern' : 'Erstellen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
